import React, {useState, useMemo, useEffect} from 'react';
import {
  useTable,
  useRowSelect,
  usePagination,
  useAsyncDebounce,
} from 'react-table';
import {X} from 'react-feather';
import {ReactComponent as SearchIcon} from '@images/search.svg';
import {ContentWrapper, Button, SelectType, Input} from '@components';
import {ReactComponent as User} from '@images/user.svg';
import {
  getProducts,
  addProductApi,
  delProductApi,
  UpdateProductApi,
  PATCHUpdateProductApi,
  ImgUploadtApi,
  getProductsWithPagination,
} from '@apis';
import Switch from 'react-switch';
import Modal from 'react-modal';
import toast from 'react-hot-toast';
import {useHistory} from 'react-router-dom';
import {useMutation, useQueryClient} from 'react-query';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';
import {getLocalizedProductName} from '../../utils/multilingual';

import {API_HOST} from '@apis/';
import {handleFileUpload, ALLOWED_IMAGE_TYPES} from '../../utils/fileUtils';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error) {
    return {hasError: true, error};
  }

  componentDidCatch(error, errorInfo) {
    console.error('Product component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-center">
          <h3 className="text-lg font-semibold text-red-600">
            Something went wrong
          </h3>
          <p className="text-gray-600 mt-2">
            Please refresh the page and try again.
          </p>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const notificationTypes = [
  {label: 'off', value: 'off'},
  {label: 'on', value: 'on'},
];

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
  },
};

const Table = ({
  columns,
  data,
  isLoading,
  controlledPageCount,
  manualPagination = false,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    setHiddenColumns,
    state: {pageIndex, pageSize},
    prepareRow,
    selectedFlatRows,
    state: {selectedRowIds},
  } = useTable(
    {
      columns,
      data,
      pageCount: controlledPageCount,
      manualPagination,
    },
    usePagination,
    useRowSelect,
  );

  return (
    <div>
      <table {...getTableProps()} className="min-w-full">
        <thead className="bg-tableHeaderBg p-4">
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps()}
                  className="text-left text-menu font-normal uppercase text-sm px-4 py-2">
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className="p-4 whitespace-nowrap text-sm">
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Products = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState(notificationTypes[0].value);

  const handleTypeSelection = item => {
    setSelectedType(item);
  };
  const history = useHistory();
  const rowclick = id => {
    var id = id;
    history.replace('/products/' + id);
  };

  const [name, setName] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [cost, setCost] = useState(0);
  const [stock, setStock] = useState(0);
  const [description, setDescription] = useState('');
  const [descriptionEs, setDescriptionEs] = useState('');
  const [enabled, setEanbled] = useState(false);
  const [gift, setGift] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [tdata, settData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [image, setImage] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [newProductID, setNewProductID] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [nextPage, setNextPage] = useState('');
  const [previousPage, setPreviousPage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [is_gift_new, setIsGiftNew] = useState(false);
  const [isEditedView, setEditedView] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form validation - check if all required fields are filled (including Spanish)
  const isFormValid = useMemo(() => {
    const isNameValid = name.trim().length > 0 && name.trim().length <= 100;
    const isNameEsValid =
      nameEs.trim().length > 0 && nameEs.trim().length <= 100;
    const isDescriptionValid =
      description.trim().length > 0 && description.trim().length <= 500;
    const isDescriptionEsValid =
      descriptionEs.trim().length > 0 && descriptionEs.trim().length <= 500;
    const isCostValid =
      cost !== null && cost !== undefined && cost !== '' && Number(cost) > 0;
    const isStockValid =
      stock !== null &&
      stock !== undefined &&
      stock !== '' &&
      Number(stock) > 0;

    return (
      isNameValid &&
      isNameEsValid &&
      isDescriptionValid &&
      isDescriptionEsValid &&
      isCostValid &&
      isStockValid
    );
  }, [name, nameEs, description, descriptionEs, cost, stock]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPageNumber(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getProductsWithPagination(
        pageNumber,
        debouncedSearch,
      );
      settData(response.results);
      setNextPage(response.next);
      setPreviousPage(response.previous);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('products.fetchError') || 'Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setIsOpen(true);
  };

  const closeModal = async () => {
    setIsOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setNameEs('');
    setCost(0);
    setStock(0);
    setDescription('');
    setDescriptionEs('');
    setEanbled(false);
    setIsGiftNew(false);
    setImage(null);
    setOriginalImageFile(null);
    setEditedView(false);
  };

  // Create product mutation
  const createProductMutation = useMutation(
    async productData => {
      const response = await addProductApi(productData);
      return response;
    },
    {
      onError: error => {
        console.error('Error creating product:', error);
        throw error;
      },
    },
  );

  // Upload image mutation
  const uploadImageMutation = useMutation(
    async ({productId, originalFile}) => {
      if (!originalFile) {
        throw new Error('No file provided for upload');
      }

      const formData = new FormData();
      formData.append('image', originalFile);

      return await ImgUploadtApi({id: productId, formdata: formData});
    },
    {
      onError: error => {
        console.error('Error uploading image:', error);
        // Only show error toast for actual failures, not JSON parsing issues
        if (error && !error.message?.includes('JSON')) {
          toast.error(
            t('products.imageUploadError') || 'Error uploading image',
          );
        }
      },
    },
  );

  const handleSaveProduct = async e => {
    e.preventDefault();

    // Validation for both English and Spanish fields
    if (
      !name.trim() ||
      !nameEs.trim() ||
      !description.trim() ||
      !descriptionEs.trim()
    ) {
      toast.error(
        t('products.inputError') ||
          'Both English and Spanish fields are required',
      );
      return;
    }

    // Check that cost and stock are greater than 0
    if (!cost || Number(cost) <= 0) {
      toast.error(
        t('products.costError') || 'Points cost must be greater than 0',
      );
      return;
    }

    if (!stock || Number(stock) <= 0) {
      toast.error(
        t('products.stockError') || 'Available stock must be greater than 0',
      );
      return;
    }

    // Allow letters, spaces, and numbers for product names
    if (/[^a-zA-Z0-9\s]/.test(name)) {
      toast.error(
        t('products.nameError') ||
          'Product name should only contain letters, numbers and spaces',
      );
      return;
    }

    setIsSaving(true);

    try {
      // Create product data
      const productData = {
        name_en: name.trim(),
        name_es_mx: nameEs.trim(),
        description_en: description.trim(),
        description_es_mx: descriptionEs.trim(),
        points_cost: parseInt(cost) || 0,
        available_stock: parseInt(stock) || 0,
        enabled: enabled,
        is_gift: is_gift_new,
      };

      // Create the product first
      const createdProduct = await createProductMutation.mutateAsync(
        productData,
      );

      // If there's an image, upload it
      if (originalImageFile && createdProduct?.id) {
        try {
          await uploadImageMutation.mutateAsync({
            productId: createdProduct.id,
            originalFile: originalImageFile,
          });
        } catch (imageError) {
          // Image upload failed but product was created successfully
          console.error('Image upload failed:', imageError);
        }
      }

      // Success - close modal and refresh data
      toast.success(t('products.productAdded') || 'Product added successfully');
      setIsOpen(false);
      resetForm();

      // Refresh the products list
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(
        typeof error === 'string'
          ? error
          : t('products.saveError') ||
              'Error saving product. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const RemoveProduct = async id => {
    try {
      await delProductApi(id);
      toast.success(t('products.productDeleted'));
      fetchData();
    } catch (error) {
      console.error('Error removing product:', error);
      toast.error(t('products.removeError') || 'Error removing product');
    }
  };

  const SubmitProductup = useMutation(params => PATCHUpdateProductApi(params));
  const submit = async (val, id) => {
    var enableStatus = false;
    if (id) {
      enableStatus = false;
    } else {
      enableStatus = true;
    }
    try {
      await SubmitProductup.mutateAsync({
        id: val.id,
        name: val.name,
        points_cost: val.cost,
        available_stock: val.stock,
        description: val.description,
        enabled: enableStatus,
      });
      fetchData();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(t('products.updateError') || 'Error updating product');
    }
  };

  const Enabled = async (row, checked) => {
    submit(row, checked);
  };

  useEffect(() => {
    fetchData();
    setIsLoaded(true);
  }, [pageNumber, debouncedSearch]); // Removed refresh dependency to avoid unnecessary re-renders

  function alphaOnly(event) {
    var value = String.fromCharCode(event.which);
    var pattern = new RegExp(/[a-zA-Z]/i);
    return pattern.test(value);
  }

  const columns = useMemo(
    () => [
      {
        Header: t('products.product'),
        accessor: 'images',
        Cell: ({row}) => (
          <div
            className="flex items-center"
            onClick={() => rowclick(row.values.id)}>
            {row.values.images[0] ? (
              <img
                src={row.values.images[0]['image']}
                alt="User avatar"
                className="w-12 h-12 object-cover rounded-lg cursor-pointer transition-transform duration-200 hover:scale-105 hover:shadow-md"
                style={{width: '50px', height: '50px'}}
              />
            ) : (
              <User className={`fill-current text-menu`} />
            )}
          </div>
        ),
      },
      {
        Header: t('products.name'),
        accessor: row => getLocalizedProductName(row),
        Cell: ({value}) => (
          <div className="flex items-center">
            <div className="px-4">
              <p className="text-left">{value}</p>
            </div>
          </div>
        ),
      },
      {
        Header: t('products.status'),
        accessor: 'enabled',
        Cell: ({row}) => (
          <div className="text-left">
            <Switch
              onChange={() => Enabled(row.values, row.values.enabled)}
              checked={row.values.enabled}
            />
          </div>
        ),
      },
      {Header: t('products.cost'), accessor: 'points_cost'},
      {Header: t('products.purchased'), accessor: 'purchased_count'},
      {Header: t('products.available'), accessor: 'available_stock'},
      {
        Header: t('products.action'),
        accessor: 'id',
        Cell: ({value}) => (
          <div className="flex items-center">
            <div className="px-4 text-left">
              <span
                role="button"
                tabIndex={0}
                className="tabbed-link"
                style={{marginLeft: -15}}
                onClick={() => RemoveProduct(value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    RemoveProduct(value);
                  }
                }}>
                {t('products.remove')}
              </span>
            </div>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <ErrorBoundary>
      <ContentWrapper
        hasSearch={false}
        breadcrumb={[
          {title: t('sidebar.menuItems.inventory'), link: '/products'},
        ]}
        addProduct={true}
        onButtonOneClick={openModal}>
        <div className="flex flex-col w-full">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
            <div
              className="relative"
              style={{minWidth: '240px', maxWidth: '320px', flex: '1'}}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('products.searchProduct')}
                className={`border border-gray-200 focus:outline-none focus:border-indigo-700 rounded w-full text-sm text-gray-700 bg-gray-100 pl-10 py-2 ${
                  searchQuery ? 'pr-9' : 'pr-3'
                }`}
              />
              <div className="text-gray-500 absolute left-3 top-0 bottom-0 flex items-center pointer-events-none">
                <SearchIcon height={16} />
              </div>
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-0 bottom-0 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={t('approve.clearSearch')}>
                  <X size={16} />
                </button>
              ) : null}
            </div>
          </div>
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            style={customStyles}
            shouldCloseOnOverlayClick={!isSaving}
            shouldCloseOnEsc={!isSaving}>
            <form onSubmit={handleSaveProduct}>
              <h2>{t('products.addProduct')}</h2>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                label={t('products.nameEnglish')}
                type="text"
                required
                disabled={isSaving}
                maxLength={100}
                placeholder={`${t('products.name')} (${t(
                  'products.maxCharacters',
                  {count: 100},
                )})`}
              />
              {name.length > 90 && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('products.charactersCount', {
                    current: name.length,
                    max: 100,
                  })}
                </p>
              )}
              <Input
                value={nameEs}
                onChange={e => setNameEs(e.target.value)}
                label={t('products.nameSpanish')}
                type="text"
                required
                disabled={isSaving}
                maxLength={100}
                placeholder={`${t('products.nameSpanish')} (${t(
                  'products.maxCharacters',
                  {count: 100},
                )})`}
              />
              {nameEs.length > 90 && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('products.charactersCount', {
                    current: nameEs.length,
                    max: 100,
                  })}
                </p>
              )}
              <Input
                value={cost}
                type="number"
                min="1"
                onChange={e => setCost(e.target.value)}
                label={t('products.pointCost')}
                disabled={isSaving}
              />
              <Input
                value={stock}
                type="number"
                min="1"
                onChange={e => setStock(e.target.value)}
                label={t('products.availableStock')}
                disabled={isSaving}
              />
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                label={t('products.descriptionEnglish')}
                required
                disabled={isSaving}
                maxLength={500}
                placeholder={`${t('products.description')} (${t(
                  'products.maxCharacters',
                  {count: 500},
                )})`}
                rows={4}
              />
              {description.length > 450 && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('products.charactersCount', {
                    current: description.length,
                    max: 500,
                  })}
                </p>
              )}
              <Input
                value={descriptionEs}
                onChange={e => setDescriptionEs(e.target.value)}
                label={t('products.descriptionSpanish')}
                required
                disabled={isSaving}
                maxLength={500}
                placeholder={`${t('products.descriptionSpanish')} (${t(
                  'products.maxCharacters',
                  {count: 500},
                )})`}
                rows={4}
              />
              {descriptionEs.length > 450 && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('products.charactersCount', {
                    current: descriptionEs.length,
                    max: 500,
                  })}
                </p>
              )}
              <Input
                type="file"
                accept={ALLOWED_IMAGE_TYPES.join(',')}
                onChange={e =>
                  handleFileUpload(
                    e,
                    setImage,
                    setOriginalImageFile,
                    setEditedView,
                    t,
                  )
                }
                label={t('products.addProductImage')}
                disabled={isSaving}
              />
              {image && (
                <div className="mt-3">
                  <img
                    src={image}
                    alt="Product preview"
                    className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <div className="checkbox-row">
                <div>
                  <label className="checkbox-label">
                    {t('products.enabled')}
                  </label>
                </div>
                <div>
                  <Input
                    type="checkbox"
                    checked={enabled}
                    onChange={e => setEanbled(!enabled)}
                    disabled={isSaving}
                  />
                </div>
              </div>
              <div className="checkbox-row">
                <div>
                  <label className="checkbox-label">
                    {t('products.isGift')}
                  </label>
                </div>
                <div>
                  <Input
                    type="checkbox"
                    checked={is_gift_new}
                    onChange={e => setIsGiftNew(!is_gift_new)}
                    disabled={isSaving}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  label={
                    isSaving
                      ? t('products.saving') || 'Saving...'
                      : t('products.save')
                  }
                  onClick={handleSaveProduct}
                  type="submit"
                  disabled={isSaving || !isFormValid}
                />
                <Button
                  label={t('common.cancel') || 'Cancel'}
                  onClick={closeModal}
                  type="button"
                  disabled={isSaving}
                  variant="secondary"
                />
              </div>
            </form>
          </Modal>
          {loading ? (
            <div>
              <h1>{t('products.loading')}</h1>
            </div>
          ) : (
            <div>
              <Table columns={columns} data={tdata} />
              {tdata && tdata.length === 0 ? (
                <div>
                  <p className="text-center mt-5">{t('products.noData')}</p>
                </div>
              ) : (
                <div>
                  <div className="bg-tableHeaderBg py-2 md:flex md:justify-center gap-x-10 px-4">
                    <button
                      style={{
                        backgroundColor: '#970c24d9',
                        color: '#fff',
                        borderRadius: 5,
                        opacity: previousPage === null ? 0.5 : 1,
                      }}
                      className={`text-xs py-2 px-4 font-bold text-center text-wine ${
                        previousPage !== null
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed'
                      }`}
                      onClick={() => {
                        if (previousPage !== null)
                          setPageNumber(pageNumber - 1);
                      }}
                      disabled={previousPage === null}>
                      {t('products.showPrevious')}
                    </button>

                    <button
                      style={{
                        backgroundColor: '#970c24d9',
                        color: '#fff',
                        borderRadius: 5,
                        opacity: nextPage === null ? 0.5 : 1,
                      }}
                      className={`text-xs py-2 px-4 font-bold text-center text-wine ${
                        nextPage !== null
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed'
                      }`}
                      onClick={() => {
                        if (nextPage !== null) setPageNumber(pageNumber + 1);
                      }}
                      disabled={nextPage === null}>
                      {t('products.showNext')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ContentWrapper>
    </ErrorBoundary>
  );
};

export default Products;
